import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { AssignmentsEnum } from "@/enum/assignments";
import { useAssignmentAbilities } from "@/hooks/use-assignment-permission";
import { PERMISSION_DENIED_LABEL } from "@/constants/permission";
import { debounce } from "lodash";
import { FileSpreadsheet, Plus, X } from "lucide-react";
import { ChangeEvent, useMemo } from "react";
import { useSectorEmailsFilterActions } from "../filter/hooks/actions";
import { AppliedSectorEmailsFilters } from "../filter/types";
import { SECTOR_EMAILS_LABELS, SECTOR_EMAILS_TOASTS } from "../messages";
import { SectorRecipientInput } from "../types";
import { FilterSelects } from "./filter-selects";
import { RecipientFormModal } from "./recipient-form-modal";

const SEARCH_DEBOUNCE_MS: number = 500;

interface DataTableToolbarProps {
  applied: AppliedSectorEmailsFilters;
  activeFiltersCount: number;
  filteredTotal: number;
  total: number;
  onExport: () => void;
  isExporting: boolean;
  onCreate: (input: SectorRecipientInput) => void;
}

export function DataTableToolbar({
  applied,
  activeFiltersCount,
  filteredTotal,
  total,
  onExport,
  isExporting,
  onCreate,
}: DataTableToolbarProps): JSX.Element {
  const { setSearch, resetFilters } = useSectorEmailsFilterActions();
  const { handleModal } = useDialogModal();
  const { canCreate } = useAssignmentAbilities(
    AssignmentsEnum.SHIPPING_RECIPIENT,
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((event: ChangeEvent<HTMLInputElement>): void => {
        setSearch(event.target.value);
      }, SEARCH_DEBOUNCE_MS),
    [setSearch]
  );

  const openCreateModal = (): void => {
    handleModal({
      isOpen: true,
      title: SECTOR_EMAILS_LABELS.ADD_RECIPIENT,
      element: <RecipientFormModal onSubmit={onCreate} />,
    });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <Input
          aria-label={SECTOR_EMAILS_LABELS.SEARCH_PLACEHOLDER}
          placeholder={SECTOR_EMAILS_LABELS.SEARCH_PLACEHOLDER}
          onChange={debouncedSearch}
          className="h-9 w-[280px]"
        />
        <FilterSelects applied={applied} />
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2"
            onClick={resetFilters}
          >
            {SECTOR_EMAILS_LABELS.CLEAR_FILTERS}
            <X className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          className="h-9 bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
          disabled={isExporting}
          onClick={() => {
            toast(SECTOR_EMAILS_TOASTS.EXPORT_REQUESTED(filteredTotal, total));
            onExport();
          }}
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          {SECTOR_EMAILS_LABELS.EXPORT_EXCEL}
        </Button>
        <Button
          size="sm"
          className="h-9"
          disabled={!canCreate}
          title={canCreate ? undefined : PERMISSION_DENIED_LABEL}
          onClick={openCreateModal}
        >
          <Plus className="mr-2 h-4 w-4" />
          {SECTOR_EMAILS_LABELS.ADD_RECIPIENT}
        </Button>
      </div>
    </div>
  );
}
